/**
 * In-app confirmation bar — the replacement for native confirm().
 *
 * Browsers can suppress native dialogs ("prevent this page from creating
 * additional dialogs"), in which case confirm() returns false immediately
 * and the guarded action silently no-ops with zero feedback. Nick hit
 * exactly that on the Schedule populate-from-document flow (PR #752);
 * this generalises that fix so every confirmation renders in-app.
 *
 * Usage — no hook wiring, callable from any handler:
 *
 *   if (!(await confirmInApp('Delete this note? This can’t be undone.', {
 *     confirmLabel: 'Delete note',
 *     danger: true,
 *   }))) return;
 *
 * <ConfirmBarHost /> mounts once at the app root (App.tsx, inside
 * ToastProvider) and renders the pending request as a fixed bottom-centre
 * amber bar (role="alertdialog", same styling as the #752 Schedule bar).
 * Escape or Cancel resolves false; a new request supersedes a pending one
 * (the superseded promise resolves false). Focus moves to Cancel so a
 * stray Enter can never confirm a destructive action.
 */
import { useEffect, useState } from 'react';
import { classNames } from '@/lib/format';

export type ConfirmInAppOptions = {
  /** Confirm button text. Be specific ("Delete note"), not "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
};

type PendingConfirm = {
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
};

/** Set by the mounted host; module-level so callers need no context. */
let pushConfirm: ((next: PendingConfirm) => void) | null = null;

export function confirmInApp(
  message: string,
  options: ConfirmInAppOptions = {},
): Promise<boolean> {
  if (!pushConfirm) {
    // Host not mounted (shouldn't happen in the app tree) — fall back to
    // the native dialog rather than silently refusing the action.
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => {
    pushConfirm!({
      message,
      confirmLabel: options.confirmLabel ?? 'Confirm',
      cancelLabel: options.cancelLabel ?? 'Cancel',
      danger: options.danger ?? false,
      resolve,
    });
  });
}

export function ConfirmBarHost() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  useEffect(() => {
    pushConfirm = (next) => {
      setPending((cur) => {
        // A second request supersedes the first; the first resolves
        // false so its caller bails out cleanly.
        if (cur) cur.resolve(false);
        return next;
      });
    };
    return () => {
      pushConfirm = null;
    };
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        pending.resolve(false);
        setPending(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  if (!pending) return null;

  const finish = (ok: boolean) => {
    pending.resolve(ok);
    setPending(null);
  };

  return (
    // Centre-screen modal (Nick, 2026-08-06): system prompts used to
    // ride the bottom edge where they were easy to miss; every
    // confirmInApp now lands mid-screen over a dim backdrop. Clicking
    // the backdrop cancels, same as Escape.
    <div
      className="fixed inset-0 z-[99] flex items-center justify-center p-4 bg-black/40"
      onClick={() => finish(false)}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={pending.message}
        onClick={(e) => e.stopPropagation()}
        className="pu-dialog flex flex-col gap-4 rounded-lg border border-kg-grey-200 dark:border-kg-grey-700 bg-white dark:bg-kg-grey-900 px-5 py-4 text-sm text-kg-black dark:text-white shadow-kg-lg w-full max-w-md"
      >
        {/* Native confirm() honoured \n in messages; keep that behaviour. */}
        <span className="min-w-0 whitespace-pre-line">{pending.message}</span>
        <span className="flex items-center justify-end gap-2">
          <button
            type="button"
            autoFocus
            onClick={() => finish(false)}
            className="kg-btn-ghost kg-btn-sm"
          >
            {pending.cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => finish(true)}
            className={classNames(
              'kg-btn-sm',
              pending.danger
                ? 'pu-btn-danger inline-flex items-center justify-center rounded-full bg-red-700 text-white uppercase tracking-widest font-medium shadow-kg-xs transition-all duration-200 ease-kg-out hover:bg-red-800 hover:shadow-kg-sm active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700'
                : 'kg-btn',
            )}
          >
            {pending.confirmLabel}
          </button>
        </span>
      </div>
    </div>
  );
}
